<template>
    <div>
        <NuxtLayout>
            <NuxtPage />
        </NuxtLayout>
    </div>
</template>

<script setup lang="ts">
    import {provide, ref, watchEffect} from 'vue';
    import {navbarUnloggedKeys} from '@navbar/public-api';
    import type {PrismicDocument} from '@prismicio/types';
    import {useAsyncData} from '#app';
    import {
        storeToRefs,
        useAuth,
        useCanonicalURL,
        useHead,
        useHostSettings,
        useLocale,
        usePrismicClient,
        usePrismicPreviewScript,
        useScripts,
    } from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {usePageCache, safelyNavigateTo} from '@malt/utils';
    import {buildHeadMetadata} from '~/composables/headMetadata';
    import {translatedUrlHelper} from '~/composables/translatedUrlHelper';
    import {HOST_SETTINGS, PRISMIC_CLIENT, PRISMIC_DOCUMENT} from '~/keys';

    const enablePageCache = useFeatureFlag('enable-page-cache');

    const authStore = useAuth();
    const {loggedIn} = storeToRefs(authStore);

    usePrismicPreviewScript();
    const client = usePrismicClient();
    provide(PRISMIC_CLIENT, client);

    const prismicDoc = ref<PrismicDocument>();
    provide(PRISMIC_DOCUMENT, prismicDoc);

    const {data: hostSettings} = await useAsyncData('host-settings', () => useHostSettings(), {deep: true});
    provide(HOST_SETTINGS, hostSettings.value!);

    const {locale} = useLocale();

    provide(navbarUnloggedKeys.TRANSLATE_URL_FN, translatedUrlHelper(client, prismicDoc).translateUrl);

    watchEffect(() => {
        if (prismicDoc.value?.type === 'redirect') {
            safelyNavigateTo(prismicDoc.value.data.redirectTo, {redirectCode: 301, externalToThisHost: true});
        }
        try {
            if (window) {
                window.dispatchEvent(new CustomEvent('Navigation'));
            }
        } catch {}
        useHead(buildHeadMetadata(prismicDoc.value, locale.value!));
        useCanonicalURL();
    });

    const {loadScripts} = useScripts(hostSettings.value);
    useHead({script: await loadScripts()});

    if (!loggedIn.value && enablePageCache.value) {
        usePageCache();
    }
</script>

<style lang="scss">
    #__nuxt {
        height: 100vh;
    }
</style>
